import {
  ContainerSizes,
  FeedbackWidget,
  PageContainer,
  PageHeader,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import { getActionsForSubMetric } from 'helpers/submetrics/getActionsForSubMetric';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ActionPlanItem } from 'types/ActionPlan';
import { FeedbackPageType } from 'types/Feedback';
import { InsightMetric } from 'types/Insights';
import { SubMetricData } from '../SubMetricData/SubMetricData';
import { InsightBreadcrumbs } from './InsightBreadcrumbs';
import { RelatedActions } from './RelatedActions';
import styles from './SubMetricDetail.module.css';

// Default metric layout, also used as the fallback when a specialized
// detail page (e.g. Microbiome Age) has no data to render.
export const DefaultSubMetricDetail = ({
  subMetric,
  allActions,
}: {
  subMetric: InsightMetric;
  allActions: ActionPlanItem[];
}) => {
  const { kitId, subMetricId } = useParams<{
    kitId: string;
    subMetricId: string;
  }>();
  const { state } = useLocation();
  const { currentKit } = useCurrentKitAndTinyAccount();
  const startAPRef = useRef(null);
  const actionItems = getActionsForSubMetric(allActions, subMetricId);

  useEffect(() => {
    if (state?.scrollTo === 'actions') {
      if (startAPRef.current) {
        const offset = (startAPRef.current as HTMLElement).offsetTop - 10;
        const scrollContainer = document.querySelector('#appWrapper');
        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: offset,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [state]);

  return (
    <PageContainer size={ContainerSizes.LG} className='pt-3'>
      <Breadcrumb
        className={styles.breadcrumb}
        overrides={{
          [subMetricId ?? '']: subMetric?.name,
        }}
      />
      <PageHeader
        title={subMetric?.name}
        beta={subMetric?.gut_vaginal_health?.beta}
      />
      <InsightBreadcrumbs subMetric={subMetric} />
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <SubMetricData
            metric={subMetric}
            relatedActionsQty={actionItems?.length ?? 0}
            refAP={startAPRef}
            kit={currentKit}
          />
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <section id='actions' ref={startAPRef}>
            <RelatedActions actions={actionItems} />
            {!!kitId && subMetric.gut_vaginal_health.id && (
              <div className={'mt-4 hide-on-desktop'}>
                <FeedbackWidget
                  kitId={kitId}
                  pageType={FeedbackPageType.INSIGHT}
                  pageId={subMetric.gut_vaginal_health.id}
                />
              </div>
            )}
          </section>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
};
